import React from "react";
import { Schema } from "yup";
import { StandardFormInput } from "modules/common/components/form/StandardFormInput";
import { PlanrInput, PlanrInputProps, PlanrTextArea, PlanrTextAreaProps } from "./Input";

/**
 * Самое частое поле формы — подпись и признак обязательности из yup-схемы,
 * ошибка под полем, внутри текстовое поле planr, связанное с Formik.
 * Сокращает связку `StandardFormInput` + `InputGroup className="planr-default-input" {...field}`.
 *
 * ```tsx
 * <FormTextField name={fields.name} schema={schema} disabled={readonly} />
 * <FormTextField name={fields.inn} schema={schema} onKeyPress={digits} maxLength={12} />
 * <FormTextField name={fields.comment} schema={schema} multiline rows={3} />
 * ```
 *
 * Любые пропсы `PlanrInput` (или `PlanrTextArea` при `multiline`) пробрасываются
 * в само поле — кроме `value`, `onChange`, `onBlur` и `name`: ими управляет Formik.
 * Два пропса есть и у подписи, и у поля, поэтому у поля они переименованы:
 * `className` и `small` относятся к подписи (`FormGroup`), а к полю —
 * `inputClassName` и `inputSmall`.
 *
 * Для нестандартного содержимого (селект, дата, число с маской) — `StandardFormInput`
 * с render-prop, как раньше.
 */
export const FormTextField = (props: FormTextFieldProps) => {
    const {
        name,
        schema,
        label,
        required,
        inline,
        small = true,
        className = "planr-form-input",
        inputClassName,
        inputSmall,
        multiline,
        ...inputProps
    } = props;

    return (
        <StandardFormInput
            name={name}
            schema={schema}
            label={label}
            required={required}
            inline={inline}
            small={small}
            className={className}
        >
            {({ field }) =>
                multiline ? (
                    <PlanrTextArea
                        id={field.name}
                        {...field}
                        value={field.value ?? ""}
                        {...(inputProps as FieldTextAreaProps)}
                        className={inputClassName}
                        small={inputSmall}
                    />
                ) : (
                    <PlanrInput
                        id={field.name}
                        {...field}
                        value={field.value ?? ""}
                        {...(inputProps as FieldInputProps)}
                        className={inputClassName}
                        small={inputSmall}
                    />
                )
            }
        </StandardFormInput>
    );
};

/** Пропсы, которыми управляет Formik или которые переименованы, чтобы не спорить с подписью. */
type Controlled = "name" | "value" | "onChange" | "onBlur" | "className" | "small";

type FieldInputProps = Omit<PlanrInputProps, Controlled>;
type FieldTextAreaProps = Omit<PlanrTextAreaProps, Controlled>;

interface FormTextFieldOwnProps {
    /** Имя поля в значениях Formik. */
    name: string;
    /** yup-схема формы: из неё берутся подпись и обязательность. */
    schema: Schema<any>;
    /** Подпись вместо той, что задана в схеме. */
    label?: React.ReactNode;
    /** Обязательность вместо той, что выводится из схемы. */
    required?: boolean;
    /** Подпись слева от поля (по умолчанию) или над ним. */
    inline?: boolean;
    /** Компактная подпись (`FormGroup` с классом `bp6-small`), по умолчанию да. */
    small?: boolean;
    /** Класс подписи (`FormGroup`), по умолчанию `planr-form-input`. */
    className?: string;
    /** Класс самого поля. */
    inputClassName?: string;
    /** Компактное поле. */
    inputSmall?: boolean;
}

export type FormTextFieldProps = FormTextFieldOwnProps &
    (({ multiline?: false } & FieldInputProps) | ({ multiline: true } & FieldTextAreaProps));
